import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Star, ArrowRight, MessageCircle, Phone, CheckCircle2, Award, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import AppointmentCTA from "../components/AppointmentCTA";
import MedicalCitations from "../components/MedicalCitations";
import FAQSection from "../components/FAQSection";
import { clinic, images, trustPoints, conditions, symptoms, diagnostics, treatmentSteps, reviews, homeFaqs, stats } from "../data/mock";
import { getWhatsAppLink } from "../lib/whatsapp";

const Home = () => {
  return (
    <div>
      <SEO path="/" faqs={homeFaqs} reviews={reviews} />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-50 via-white to-sky-100">
        <div className="absolute top-10 right-0 w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl" />
        <div className="container-app relative grid lg:grid-cols-2 gap-10 items-center py-14 lg:py-20">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 bg-white text-sky-600 font-semibold text-sm px-4 py-2 rounded-full shadow-sm border border-sky-100">
              <ShieldCheck size={16} /> Trusted Liver & Gastro Specialist
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-slate-900 mt-5 leading-[1.1]">
              Best <span className="text-sky-500">Liver Doctor</span> in Varanasi
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
              {clinic.doctorName} ({clinic.qualifications}) offers expert diagnosis and personalized treatment for fatty liver, cirrhosis, hepatitis, jaundice and all liver & digestive disorders — backed by {clinic.experience} years of experience.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 h-13 py-6 text-base font-semibold shadow-lg shadow-sky-500/30 w-full sm:w-auto">
                  <MessageCircle size={20} className="mr-2" /> Book Appointment
                </Button>
              </a>
              <a href={`tel:${clinic.phone.replace(/\s/g, "")}`}>
                <Button size="lg" variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50 rounded-full px-8 h-13 py-6 text-base font-semibold w-full sm:w-auto">
                  <Phone size={20} className="mr-2" /> Call Now
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-amber-400 text-amber-400" />)}
              </div>
              <span className="text-sm text-slate-600"><span className="font-bold text-slate-900">4.9/5</span> from 500+ happy patients</span>
            </div>
          </div>

          <div className="relative flex justify-center lg:justify-end">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-400 to-sky-200 rounded-[2.5rem] rotate-3 scale-95 opacity-40 blur-xl" />
            <div className="relative bg-gradient-to-b from-sky-100 to-white rounded-[2.5rem] p-4 shadow-2xl shadow-sky-200/50">
              <img src={images.doctor} alt={clinic.doctorName} className="w-full max-w-sm rounded-[2rem] object-cover" />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 animate-floaty">
                <div className="w-11 h-11 rounded-full bg-sky-500 grid place-items-center text-white"><Award size={22} /></div>
                <div>
                  <div className="font-heading font-bold text-slate-900 leading-tight">{clinic.experience} Years</div>
                  <div className="text-xs text-slate-500">Clinical Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-sky-600">
        <div className="container-app grid grid-cols-2 lg:grid-cols-4 divide-x divide-sky-500/40">
          {stats.map((s) => (
            <div key={s.label} className="py-8 text-center">
              <div className="font-heading text-3xl sm:text-4xl font-bold text-white">{s.value}</div>
              <div className="text-sky-100 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY TRUST */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <span className="inline-flex items-center gap-2 text-sky-600 font-semibold text-sm bg-sky-50 px-4 py-1.5 rounded-full">
              <ShieldCheck size={16} /> Expert Liver Care in Varanasi
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4 mb-6">Comprehensive, Compassionate Liver Care You Can Trust</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-left sm:text-center">
              <p>Your liver is one of the hardest-working organs in your body — filtering toxins, aiding digestion, storing energy and supporting hundreds of vital functions. Yet liver disease often develops silently, showing no symptoms until significant damage has occurred. That is why expert evaluation and early detection matter so much.</p>
              <p>{clinic.doctorName} is a highly experienced Gastroenterologist and Liver Specialist (Hepatologist) in Varanasi, offering complete care for fatty liver, cirrhosis, hepatitis, jaundice, liver infections and complex digestive disorders. Combining advanced diagnostics like FibroScan and endoscopy with genuinely personalized treatment, every patient receives an accurate diagnosis, a clear explanation of their condition and a realistic, achievable plan to protect their liver for life.</p>
            </div>
          </div>
          <SectionHeading eyebrow="Why Choose Us" title="Why Patients Trust Dr. Indresh Dixit"
            subtitle="Compassionate, accurate and evidence-based liver care that puts your health first." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustPoints.map((t, i) => (
              <div key={i} className="group bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 grid place-items-center mb-5 group-hover:bg-sky-500 group-hover:text-white transition-colors">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{t.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Liver Conditions Treated" title="Comprehensive Liver & Digestive Care"
            subtitle="From early fatty liver to complex chronic liver disease — expert treatment under one roof." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.map((c, i) => {
              const Icon = Icons[c.icon] || Icons.Activity;
              return (
                <Link key={i} to={c.link} className="group bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:border-sky-200 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white grid place-items-center mb-5 shadow-lg shadow-sky-500/30">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{c.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-4">{c.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sky-600 font-semibold text-sm group-hover:gap-2 transition-all">
                    Learn More <ArrowRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="py-16 sm:py-20">
        <div className="container-app grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading center={false} eyebrow="Warning Signs" title="Common Liver Disease Symptoms"
              subtitle="Don't ignore these signs. Early detection can prevent serious liver damage." />
            <div className="grid grid-cols-2 gap-3">
              {symptoms.map((s, i) => {
                const Icon = Icons[s.icon] || Icons.Activity;
                return (
                  <div key={i} className="flex items-center gap-3 bg-sky-50/60 border border-sky-100 rounded-xl px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-white text-sky-600 grid place-items-center shrink-0 shadow-sm"><Icon size={18} /></div>
                    <span className="text-sm font-medium text-slate-700">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative">
            <img src={images.liverAnatomy} alt="Liver and gallbladder anatomy" className="w-full rounded-3xl shadow-xl border border-slate-100" />
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full shadow-lg px-6 py-3 text-center border border-sky-100">
              <span className="text-sm font-semibold text-sky-600">Understand Your Liver Health</span>
            </div>
          </div>
        </div>
      </section>

      {/* DIAGNOSTICS */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="container-app">
          <SectionHeading eyebrow="Diagnostics" title="Advanced Liver Diagnosis & Investigations"
            subtitle="State-of-the-art tests for accurate, early and reliable diagnosis." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {diagnostics.map((d, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-heading text-sky-500 font-bold text-xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{d.title}</h3>
                </div>
                <p className="text-slate-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENT PROCESS */}
      <section className="py-16 sm:py-20">
        <div className="container-app">
          <SectionHeading eyebrow="Consultation Process" title="Your Journey to Better Liver Health"
            subtitle="A simple, transparent and personalized 4-step approach." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {treatmentSteps.map((s, i) => (
              <div key={i} className="relative bg-gradient-to-b from-sky-50 to-white rounded-2xl p-7 border border-sky-100">
                <div className="font-heading text-5xl font-bold text-sky-200 mb-3">{s.step}</div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-16 sm:py-20 bg-slate-900">
        <div className="container-app">
          <SectionHeading light eyebrow="Patient Reviews" title="What Our Patients Say"
            subtitle="Real experiences from patients across Varanasi." />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 -mt-4">
            <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-full pl-4 pr-6 py-2.5">
              <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-6 h-6" />
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-white text-lg">{clinic.googleRating}</span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => <Star key={j} size={15} className="fill-amber-400 text-amber-400" />)}
                </div>
                <span className="text-slate-400 text-sm">({clinic.googleReviewCount})</span>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((r, i) => (
              <div key={i} className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1">
                    {[...Array(r.rating)].map((_, j) => <Star key={j} size={16} className="fill-amber-400 text-amber-400" />)}
                  </div>
                  <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-5 h-5 opacity-90" />
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-5">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
                  <div className="w-10 h-10 rounded-full bg-sky-500 grid place-items-center text-white font-heading font-bold">{r.name.charAt(0)}</div>
                  <div>
                    <div className="font-semibold text-white text-sm">{r.name}</div>
                    <div className="text-slate-400 text-xs">{r.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href={clinic.googleReviewsUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-slate-800 hover:bg-slate-100 rounded-full px-7 h-12 font-semibold shadow-lg">
                <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="w-5 h-5 mr-2" />
                View All Reviews on Google
              </Button>
            </a>
          </div>
        </div>
      </section>

      <FAQSection faqs={homeFaqs} subtitle="Answers to the most common questions about liver care in Varanasi." />
      <MedicalCitations />
      <AppointmentCTA />
    </div>
  );
};

export default Home;
